// redux/slices/permissionsSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiRequest } from "../../Utils/apiClient";
import { clearJwtToken } from "../../Utils/authStorage";
import { resetStore } from "./resetStore";

/* ===================== HELPERS ===================== */

const getError = (error, fallback) =>
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;

const handleAuthError = async (error) => {
    const status = error?.response?.status;

    if (status === 401 || status === 403) {
        try {
            await clearJwtToken();
        } catch { }
    }
};

/* ===================== THUNKS ===================== */

export const fetchPermissions = createAsyncThunk(
    "permissions/fetchPermissions",
    async (employeeUid, { rejectWithValue }) => {
        try {
            if (!employeeUid) throw new Error("Employee UID is required");

            const res = await apiRequest(
                "GET",
                `/employee-permissions/${employeeUid}`
            );

            return res?.data || res;
        } catch (error) {
            await handleAuthError(error);

            return rejectWithValue(
                getError(error, "Failed to fetch permissions")
            );
        }
    }
);

export const updatePermissions = createAsyncThunk(
    "permissions/updatePermissions",
    async (
        { employeeUid, masterUid, permissions },
        { rejectWithValue }
    ) => {
        try {
            if (!employeeUid) throw new Error("Employee UID is required");

            const res = await apiRequest(
                "PUT",
                `/employee-permissions/${employeeUid}`,
                {
                    masterUid,
                    ...permissions,
                }
            );

            return res?.data || res;
        } catch (error) {
            await handleAuthError(error);

            return rejectWithValue(
                getError(error, "Failed to update permissions")
            );
        }
    }
);

/* ===================== STATE ===================== */

const initialState = {
    byUid: {},
    status: "idle",
    error: null,
};

/* ===================== SLICE ===================== */

const permissionsSlice = createSlice({
    name: "permissions",

    initialState,

    reducers: {
        clearPermissionsError(state) {
            state.error = null;
        },

        resetPermissionsStatus(state) {
            state.status = "idle";
            state.error = null;
        },

        clearPermissions(state) {
            state.byUid = {};
            state.status = "idle";
            state.error = null;
        },
    },

    extraReducers: (builder) => {
        builder

            .addCase(fetchPermissions.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })

            .addCase(fetchPermissions.fulfilled, (state, action) => {
                state.status = "succeeded";

                const permission = action.payload;

                if (permission?.employeeUid) {
                    state.byUid[permission.employeeUid] = permission;
                }
            })

            .addCase(fetchPermissions.rejected, (state, action) => {
                state.status = "failed";

                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Failed to fetch permissions";
            })

            .addCase(updatePermissions.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })

            .addCase(updatePermissions.fulfilled, (state, action) => {
                state.status = "succeeded";

                const permission =
                    action.payload?.permissions || action.payload;

                if (permission?.employeeUid) {
                    state.byUid[permission.employeeUid] = permission;
                }
            })

            .addCase(updatePermissions.rejected, (state, action) => {
                state.status = "failed";

                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Failed to update permissions";
            })

            .addCase(resetStore, () => initialState);
    },
});

/* ===================== ACTIONS ===================== */

export const {
    clearPermissionsError,
    resetPermissionsStatus,
    clearPermissions,
} = permissionsSlice.actions;

/* ===================== SELECTORS ===================== */

export const selectPermissionsState = (state) =>
    state.permissions;

export const selectPermissionsStatus = (state) =>
    state.permissions?.status || "idle";

export const selectPermissionsError = (state) =>
    state.permissions?.error || null;

export const selectPermissionsByUid = (employeeUid) => (state) =>
    state.permissions?.byUid?.[employeeUid] || null;

export default permissionsSlice.reducer;